// WHO IS TOLD OF THE OWNER/GC SIGN-OFF (owner, 2026-10-21: "add email
// notification to a list of recipients, that get sent, saying owner signed,
// on this system, area, activity"). One list per project, kept beside the
// switches: anyone on the roster, or any address typed in. The mail goes when
// a level, area or system is switched on, its day moves, or it is switched
// off (back/utils/ownerSignOffNotify) - the person who switches it gets no echo.
//
// IN THE PAGE'S TOP BAR (owner, 2026-10-21: "they need to be added similar to
// issue and shop drawings on the top of levels, areas, and systems"): one
// button for the one list, whichever level is open - `variant="button"`.
import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Select, Tooltip, message } from "antd";
import { MailOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { getOwnerSignOffRecipients, setOwnerSignOffRecipients } from "api/ownerSignOff/ownerSignOffApi";
import { useRoster, filterPeopleOption, renderPeopleOption } from "components/PeoplePicker/PeoplePicker";
import { peoplePickerOptions, peopleOf, emailValue } from "components/PeoplePicker/peoplePickerOptions";

export default function OwnerSignOffRecipients({ projectId, canSign = false, variant = "line" }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const roster = useRoster(projectId);

  useEffect(() => {
    let alive = true;
    if (!projectId || !canSign) { setList(null); return undefined; }
    getOwnerSignOffRecipients(projectId)
      .then((d) => { if (alive) setList(d.recipients || []); })
      .catch(() => { if (alive) setList(null); });
    return () => { alive = false; };
  }, [projectId, canSign]);

  const people = useMemo(() => {
    const out = new Map();
    (roster || []).forEach((g) => peopleOf(g).forEach((u) => {
      const email = String(u.email || "").toLowerCase();
      if (email) out.set(email, { email, userId: String(u.userId || ""), name: u.name || email, companyId: String(g.companyId || "") });
    }));
    return out;
  }, [roster]);
  const options = useMemo(() => peoplePickerOptions(roster, { personValue: emailValue }), [roster]);

  if (!canSign || list === null) return null;
  const names = list.map((r) => r.name || r.email).filter(Boolean);
  const save = async () => {
    setSaving(true);
    try {
      const entries = (editing || []).map((v) => {
        const email = String(v || "").trim().toLowerCase();
        return people.get(email) || { email };
      });
      const d = await setOwnerSignOffRecipients(projectId, entries);
      setList(d.recipients || []);
      setEditing(null);
      if (d.rejected && d.rejected.length) message.warning(`Not added: ${d.rejected.map((r) => `${r.value || "(blank)"} - ${r.why}`).join("; ")}`);
      else message.success(`${(d.recipients || []).length} will be emailed when something is signed off`);
    } catch (e) {
      message.error(e.message || "Could not save the list");
    } finally {
      setSaving(false);
    }
  };

  const open = () => setEditing(list.map((r) => String(r.email || "").toLowerCase()).filter(Boolean));
  const trigger = variant === "button" ? (
    <Tooltip title={names.length
      ? `Emailed when a level, area or system is signed off by owner/GC or withdrawn: ${names.join(", ")}`
      : "Nobody is emailed when a level, area or system is signed off by owner/GC yet"}>
      <Button icon={<UsergroupAddOutlined />} onClick={open} data-testid="owner-signoff-recipients-button">
        Sign-off recipients{names.length ? ` (${names.length})` : ""}
      </Button>
    </Tooltip>
  ) : (
    <>
      <MailOutlined /> {names.length
        ? <>Emailed on sign-off: <b>{names.slice(0, 3).join(", ")}{names.length > 3 ? ` +${names.length - 3}` : ""}</b></>
        : "Nobody is emailed on sign-off yet"}
      {" "}
      <Button size="small" type="link" style={{ padding: 0, height: "auto", fontSize: 11.5 }} onClick={open}>
        {names.length ? "Edit" : "Add people"}
      </Button>
    </>
  );
  return (
    <div style={variant === "button" ? { display: "inline-block" } : { fontSize: 11.5, color: "#475569", marginTop: 6 }} data-testid="owner-signoff-recipients">
      {trigger}
      <Modal
        title="Who is emailed when the owner/GC signs off"
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={save}
        okText="Save"
        confirmLoading={saving}
        destroyOnClose
      >
        <p style={{ fontSize: 12.5, color: "#555" }}>
          When a level, area or system is switched on here, or its day is moved, everyone on this list gets an email
          saying what was signed off, on which day, the schedule activities it stars and how many issues and shop
          drawings it covers. Switching one off emails the same list that it was withdrawn and what it had covered.
          People on the project also see it in their notifications. You are not emailed for your own switch.
        </p>
        <Select
          mode="tags"
          style={{ width: "100%" }}
          tokenSeparators={[",", " "]}
          value={editing || []}
          onChange={setEditing}
          placeholder="Search the project's people, or type any address"
          filterOption={filterPeopleOption}
          optionRender={renderPeopleOption}
          options={options}
          aria-label="Owner/GC sign-off recipients"
        />
      </Modal>
    </div>
  );
}
